import React from "react"
import CardFrame from "./CardFrame"
import { formatValue, formatChange } from "../format"
import useDashboardTexts from "../useDashboardTexts"

const COLOR = "#3b6fd4"
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

export const SEGMENTS = [
  ["hour_of_day", "Hour of the day"],
  ["weekday", "Day of the week"],
  ["month_of_year", "Month of the year"],
]

// a country's code, as its name in the browser's language
let regionNames = null
try {
  regionNames = new Intl.DisplayNames(undefined, { type: "region" })
} catch {}
export const readable = (segment, label) => {
  if (segment === "country" && /^[A-Z]{2}$/.test(label)) return regionNames?.of(label) || label
  return label
}

// a weekday's or month's short name in the reader's language (t: the dashboards' texts)
const labelOf = (segment, key, t = (text) => text) => {
  if (segment === "hour_of_day") return `${key}:00`
  if (segment === "weekday") return WEEKDAYS[Number(key) - 1] ? t(WEEKDAYS[Number(key) - 1]) : key
  if (segment === "month_of_year") return MONTHS[Number(key) - 1] ? t(MONTHS[Number(key) - 1]) : key
  return key
}

/**
 * A metric over the period, folded onto a cycle — which hour of the day,
 * which day of the week, which month of the year it happens in — the way the
 * Sales section groups it. Each segment: its figure, its share of the whole
 * (for a metric that adds up), and its change on the compared period.
 */
export default function SegmentsCard({ card, def, data, loading, comparing, dimensions, onEdit }) {
  const t = useDashboardTexts()
  const unit = def?.unit
  const keys = data?.keys || []
  const max = Math.max(0, ...(data?.values || []).map((v) => Math.abs(v)))
  const segmentId = data?.segment || card.segment
  // a time segment (hour, weekday, month) or a dimension (shipping method, customer …)
  const byTime = SEGMENTS.some(([id]) => id === segmentId)
  const segmentLabel = t(SEGMENTS.find(([id]) => id === segmentId)?.[1] || dimensions?.find((d) => d.id === segmentId)?.label || "")
  // a device kind (Mobile, Tablet, Desktop) is a word of the dashboards' own: in the reader's language
  const nameOf = (key, i) => (byTime ? labelOf(data.segment, key, t) : segmentId === "device" ? t(data.labels?.[i] ?? key) : readable(segmentId, data.labels?.[i] ?? key))

  return (
    <CardFrame title={(card.title && t(card.title)) || t(def?.label) || card.metric} subtitle={segmentLabel ? t("by %s", segmentLabel.toLowerCase()) : ""} onEdit={onEdit} testId="dashboard-segments">
      {data?.error ? (
        <span className="text-sm text-admin_error">{t`Could not load`}</span>
      ) : (
        <>
          <div className="flex items-baseline gap-3 mb-3">
            <span className="text-[24px] font-semibold text-admin_text tabular-nums">{data ? formatValue(data.total, unit) : "…"}</span>
            {comparing && data?.previousTotal !== undefined && (() => {
              const change = formatChange(data.total, data.previousTotal)
              return change && <span className={`text-sm font-semibold ${change.className}`}>{change.text}</span>
            })()}
          </div>
          {/* the segments side by side, as columns: the share over each, the
              value and change under it (hover a column for the earlier figure) */}
          <div className={`${byTime ? "hidden md:flex" : "hidden"} items-stretch gap-1 overflow-x-auto pb-1 ${loading ? "opacity-40" : ""}`}>
            {keys.map((key, i) => {
              const value = data.values[i]
              const share = data.shares?.[i]
              const before = data.previousValues?.[i]
              // an empty segment in both periods has nothing to say: no share, no change, dimmed
              const empty = !value && !before
              const change = comparing && data.previousValues && !empty ? formatChange(value, before) : null
              const dense = keys.length > 12
              return (
                <div
                  key={key}
                  className={`flex-1 flex flex-col items-center gap-1 ${dense ? "min-w-[26px]" : "min-w-[44px]"} ${empty ? "opacity-50" : ""}`}
                  title={`${labelOf(data.segment, key, t)}: ${formatValue(value, unit)}${share > 0 ? ` · ${share.toFixed(1)}%` : ""}${data.previousValues ? ` · before ${formatValue(before, unit)}` : ""}`}
                  data-testid="segment-row"
                >
                  <span className="h-4 text-[11px] font-semibold text-admin_text tabular-nums whitespace-nowrap">{share > 0 ? `${share.toFixed(share >= 10 ? 0 : 1)}%` : ""}</span>
                  <span className="relative w-full h-[140px] rounded bg-admin_neutral_1/60 overflow-hidden">
                    <span className="absolute inset-x-0 bottom-0 rounded" style={{ height: `${max ? (Math.abs(value) / max) * 100 : 0}%`, background: COLOR, opacity: 0.85 }} />
                  </span>
                  <span className="text-[11px] text-admin_text_secondary tabular-nums whitespace-nowrap">{dense ? key : labelOf(data.segment, key, t)}</span>
                  {!dense && (
                    <>
                      <span className="text-[12px] text-admin_text tabular-nums whitespace-nowrap">{empty ? "" : formatValue(value, unit, { compact: true })}</span>
                      <span className={`h-4 text-[10px] font-semibold whitespace-nowrap ${change?.className || ""}`}>{change?.text || ""}</span>
                    </>
                  )}
                </div>
              )
            })}
          </div>
          {/* on a phone, one row per segment: too narrow for columns */}
          <div className={`${byTime ? "md:hidden" : ""} flex flex-col gap-1 max-h-[380px] overflow-y-auto pr-1 ${loading ? "opacity-40" : ""}`}>
            {keys.map((key, i) => {
              const value = data.values[i]
              const share = data.shares?.[i]
              const before = data.previousValues?.[i]
              const empty = !value && !before
              const change = comparing && data.previousValues && !empty ? formatChange(value, before) : null
              return (
                <div key={key} className={`grid ${byTime ? "grid-cols-[52px_minmax(0,1fr)_auto]" : "grid-cols-[minmax(0,160px)_minmax(0,1fr)_auto]"} items-center gap-2 text-[13px] ${empty ? "opacity-50" : ""}`} data-testid="segment-row-phone">
                  <span className={`text-admin_text_secondary tabular-nums ${byTime ? "" : "truncate"}`} title={nameOf(key, i)}>{nameOf(key, i)}</span>
                  <span className="relative h-6 rounded bg-admin_neutral_1/60 overflow-hidden">
                    <span className="absolute inset-y-0 left-0 rounded" style={{ width: `${max ? (Math.abs(value) / max) * 100 : 0}%`, background: COLOR, opacity: 0.85 }} />
                    {share > 0 && (
                      <span className="absolute inset-y-0 left-2 flex items-center text-[11px] font-semibold text-admin_text">
                        <span className="px-1 rounded bg-admin_paper/80">{share.toFixed(share >= 10 ? 0 : 1)}%</span>
                      </span>
                    )}
                  </span>
                  <span className="flex items-baseline gap-2 justify-end tabular-nums min-w-[96px]">
                    <span className="text-admin_text">{formatValue(value, unit)}</span>
                    {change && <span className={`text-[11px] font-semibold ${change.className}`}>{change.text}</span>}
                  </span>
                </div>
              )
            })}
          </div>
        </>
      )}
    </CardFrame>
  )
}
